import React from "react";
import TextInput from "../../../../../../components/common/PL/TextInput/TextInput";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CloseIcon from "@material-ui/icons/Close";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import AddIcon from "@material-ui/icons/Add";
import { CircularProgress } from "@material-ui/core";
import "./EmpresaSearch.css";

type EmpresaSearchProps<T extends { id: string }> = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedEmpresa: T | null;
  onSelect: (empresa: T) => void;
  onClear: () => void;
  options: T[];
  getLabel: (empresa: T) => string;
  placeholder?: string;
  /** Ícono de cada fila del dropdown. */
  icon?: React.ReactNode;
  /** Texto cuando la búsqueda no trae resultados. */
  emptyLabel?: string;
  /** Etiqueta de la fila de alta; se le concatena el texto buscado. */
  addNewLabel?: string;
  disabled?: boolean;
  isLarge?: boolean;
  onAddNew?: (searchText: string) => void;
  /** Desactivar cuando `options` ya viene filtrado por el backend (búsqueda con debounce por
   * `searchValue`): filtrar de nuevo acá con matching exacto de substring puede descartar
   * resultados válidos que el backend matcheó con una lógica distinta (fuzzy, etc). */
  filterLocally?: boolean;
  /** Muestra feedback mientras el caller confirma la selección contra el backend. */
  loading?: boolean;
};

const EmpresaSearch = <T extends { id: string }>({
  searchValue,
  onSearchChange,
  selectedEmpresa,
  onSelect,
  onClear,
  options,
  getLabel,
  placeholder = "-completar-",
  icon = <LocalShippingOutlinedIcon fontSize="small" />,
  emptyLabel = "No se encontraron empresas",
  addNewLabel = "Agregar nueva empresa",
  disabled = false,
  isLarge = false,
  onAddNew,
  filterLocally = true,
  loading = false,
}: EmpresaSearchProps<T>) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const optionRefs = React.useRef<Array<HTMLDivElement | null>>([]);

  // El chevron abre el dropdown sin pasar por el input (el click queda en el ícono), así que
  // hay que enfocarlo a mano para que el guardia pueda tipear de inmediato.
  React.useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Close dropdown on click outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // El cierre de arriba sólo cubre el mouse (clic afuera). Con teclado no hay ningún "clic": si el
  // guardia sigue tabulando hacia otro campo — o si el foco se pierde solo por cualquier otro
  // motivo (p. ej. este mismo input deshabilitándose al seleccionar) — nada cerraba el
  // desplegable, así que quedaba abierto para siempre. `focusout` (a diferencia de `blur`, sí
  // burbujea) dispara siempre que el foco sale del contenedor, sea cual sea la causa.
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleFocusOut = (event: FocusEvent) => {
      const nextFocused = event.relatedTarget as Node | null;
      if (!nextFocused || !container.contains(nextFocused)) {
        setIsOpen(false);
      }
    };
    container.addEventListener("focusout", handleFocusOut);
    return () => {
      container.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  const filteredEmpresas = React.useMemo(() => {
    if (!filterLocally || !searchValue.trim()) {
      return options;
    }
    const cleanSearch = searchValue.toLowerCase().trim();
    return options.filter((emp) => getLabel(emp).toLowerCase().includes(cleanSearch));
  }, [filterLocally, searchValue, options, getLabel]);

  React.useEffect(() => {
    setFocusedIndex(isOpen && filteredEmpresas.length > 0 ? 0 : -1);
  }, [isOpen, filteredEmpresas]);

  React.useEffect(() => {
    if (focusedIndex >= 0) {
      optionRefs.current[focusedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  const handleInputFocus = () => {
    if (!disabled && !selectedEmpresa) {
      setIsOpen(true);
    }
  };

  // Al seleccionar, el input pasa a `disabled` (queda con el ícono de "limpiar"). Un input
  // enfocado que se deshabilita pierde el foco solo — el navegador no se lo da a nadie ("cae" al
  // body) — así que el próximo Tab del guardia no tiene desde dónde continuar y la navegación por
  // teclado se corta ahí. Por eso el foco se mueve a mano al siguiente campo tabulable ANTES de
  // que el input se deshabilite, mientras todavía es un elemento enfocable válido en el DOM.
  const focusNextTabbable = () => {
    const currentEl = inputRef.current;
    if (!currentEl) return;
    const focusableSelector =
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = Array.from(
      document.querySelectorAll<HTMLElement>(focusableSelector)
    ).filter(
      (el) =>
        el.offsetParent !== null &&
        // El próximo tabulable en el DOM en este instante puede ser el título-botón de la
        // siguiente tarjeta (todavía colapsada), no un campo real: React no re-renderizó todavía
        // con la empresa recién elegida, así que nada de lo que ésta desbloquea existe aún acá.
        // Enfocarlo por accidente le prende el estado focus-visible de MUI (fondo gris) sin que el
        // guardia haya tocado nada.
        !el.closest(".MuiAccordionSummary-root")
    );
    const currentIndex = focusable.indexOf(currentEl);
    if (currentIndex === -1) return;
    focusable[currentIndex + 1]?.focus();
  };

  const handleSelectEmpresa = (empresa: T) => {
    onSelect(empresa);
    setIsOpen(false);
    focusNextTabbable();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear();
    setIsOpen(false);
  };

  const displayValue = selectedEmpresa ? getLabel(selectedEmpresa) : searchValue;

  // Render right icon depending on state
  const rightIcon = loading ? (
    <CircularProgress size={20} aria-label="Seleccionando vehículo" />
  ) : selectedEmpresa ? (
    <CloseIcon
      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      onClick={disabled ? undefined : handleClear}
    />
  ) : (
    <ExpandMoreIcon
      style={{
        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s ease",
        cursor: "pointer",
      }}
      // Mismo motivo que en las filas del desplegable: sin esto, el mousedown le saca el foco al
      // input, el `focusout` cierra el desplegable de entrada, y el click que sigue lo vuelve a
      // abrir — el guardia ve el chevron "parpadear" en vez de cerrarlo cuando quiere cerrarlo él.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => !disabled && setIsOpen(!isOpen)}
    />
  );

  return (
    <div className="transportSearchWrapper" ref={containerRef}>
      <TextInput
        inputRef={inputRef}
        value={displayValue}
        onChange={(val) => {
          if (!selectedEmpresa) {
            onSearchChange(val);
            setIsOpen(true);
          }
        }}
        placeholder={placeholder}
        leftIcon={<SearchIcon />}
        rightIcon={rightIcon}
        disabled={disabled || !!selectedEmpresa}
        onKeyDown={(e) => {
          if (disabled || selectedEmpresa) return;

          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
              return;
            }
            if (filteredEmpresas.length === 0) return;

            setFocusedIndex((currentIndex) => {
              if (currentIndex === -1) {
                return e.key === "ArrowDown" ? 0 : filteredEmpresas.length - 1;
              }
              const direction = e.key === "ArrowDown" ? 1 : -1;
              return (currentIndex + direction + filteredEmpresas.length) % filteredEmpresas.length;
            });
            return;
          }

          if (e.key === "Home" || e.key === "End") {
            e.preventDefault();
            if (!isOpen) setIsOpen(true);
            setFocusedIndex(e.key === "Home" ? 0 : filteredEmpresas.length - 1);
            return;
          }

          if (e.key === "Escape") {
            setIsOpen(false);
            return;
          }

          // Atajo: Enter selecciona la primera coincidencia igual que un click, o si no hay
          // ninguna, dispara el alta igual que clickear "agregar nuevo" (con lo tipeado como
          // prefill). Sin texto tipeado no hace nada: elegir "la primera opción" a ciegas sería
          // arbitrario.
          if (e.key === "Enter") {
            e.preventDefault();
            if (focusedIndex >= 0 && filteredEmpresas[focusedIndex]) {
              handleSelectEmpresa(filteredEmpresas[focusedIndex]);
              return;
            }
            if (!searchValue.trim()) return;
            if (filteredEmpresas.length > 0) {
              handleSelectEmpresa(filteredEmpresas[0]);
            } else if (onAddNew) {
              onAddNew(searchValue.trim());
              setIsOpen(false);
            }
            return;
          }

          if (!isOpen) {
            setIsOpen(true);
          }
        }}
        inputStyle={{
          cursor: selectedEmpresa ? "default" : "text",
          ...(isLarge
            ? {
                minHeight: 44,
                height: 40,
                fontSize: 24,
              }
            : {}),
        }}
      />
      {isOpen && !selectedEmpresa && !disabled && (
        <div className="transportSearchDropdown">
          {filteredEmpresas.length > 0 ? (
            filteredEmpresas.map((emp, index) => (
              <div
                key={emp.id}
                ref={(element) => {
                  optionRefs.current[index] = element;
                }}
                className={`transportSearchItem ${
                  focusedIndex === index ? "transportSearchItemFocused" : ""
                } ${isLarge ? "transportSearchItemLarge" : ""}`}
                onClick={() => handleSelectEmpresa(emp)}
                // Sin esto, el mousedown le saca el foco al input (el div no es enfocable) antes
                // de que llegue el click: dispara el `focusout` de arriba, que cierra el
                // desplegable y borra esta fila del DOM — el click nunca llega a ejecutarse y la
                // selección con mouse queda muerta.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setFocusedIndex(index)}
                role="option"
                aria-selected={focusedIndex === index}
              >
                <div className="transportSearchItemIcon">{icon}</div>
                {getLabel(emp)}
              </div>
            ))
          ) : (
            <div
              className={`transportSearchItem ${isLarge ? "transportSearchItemLarge" : ""}`}
              style={{ cursor: "default", color: "#94A3B8" }}
            >
              {emptyLabel}
            </div>
          )}
          {onAddNew && (
            <div
              className={`transportSearchItem transportSearchAddNew ${
                isLarge ? "transportSearchItemLarge" : ""
              }`}
              onClick={() => {
                onAddNew(searchValue.trim());
                setIsOpen(false);
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <div className="transportSearchItemIcon">
                <AddIcon fontSize="small" />
              </div>
              {addNewLabel}
              {searchValue.trim() ? ` "${searchValue.trim()}"` : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmpresaSearch;
